import React, { useState } from "react";
import { ArrowRight, BarChart3, Calendar, Eye, EyeOff, FileText, Lock, Mail, Users } from "lucide-react";
import { practiceApi } from "../lib/practiceApi";
import "../pages/login.css";

const FEATURES = [[FileText, "Organizza"], [Calendar, "Gestisci"], [Users, "Collabora"], [BarChart3, "Cresci"]];

const GoogleMark = () => <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
  <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.5 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.6 9.5 24 9.5z" />
  <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-2.8-.4-4.1H24v8.1h12.5c-.3 2.1-1.6 5.2-4.6 7.3l7.6 5.9c4.5-4.2 6.6-10.3 6.6-17.2z" />
  <path fill="#FBBC05" d="M10.4 28.7A14.4 14.4 0 0 1 9.6 24c0-1.6.3-3.2.8-4.7l-7.8-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.8-6.1z" />
  <path fill="#34A853" d="M24 48c6.2 0 11.5-2 15.5-5.6l-7.6-5.9c-2 1.4-4.7 2.4-7.9 2.4-6.4 0-11.7-3.7-13.6-9.8l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
</svg>;

export function LoginView({ login, setLogin, submit, error, googleError }) {
  const [visible, setVisible] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function forgotSend() {
    setSending(true);
    try { await practiceApi("/auth/forgot-password", { method: "POST", body: JSON.stringify({ email: login.email }) }); }
    catch (e) { /* risposta sempre generica */ }
    setSent(true); setSending(false);
  }

  function google() {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }

  return <main className="login-shell" data-testid="login-page">
    <section className="login-visual">
      <div className="login-side">
        <img src="/brand/ingego-logo-light.png" alt="ingego" className="login-brand-logo" />
        <div className="login-hero">
          <p className="eyebrow">Gestionale per studi tecnici</p>
          <h1>Il tuo studio,<i>sotto controllo.</i></h1>
          <p>Progetti, pratiche, scadenze e persone.<br />Tutto in un unico posto.</p>
          <div className="login-rule" />
        </div>
        <div className="login-features">
          {FEATURES.map(([Icon, label]) => <span key={label}><Icon size={17} strokeWidth={1.6} />{label}</span>)}
        </div>
        <div className="login-legal"><span>ingego</span><span>Engineering, organized.</span></div>
      </div>
      <div className="login-art"><img src="/brand/login-hero.png" alt="" aria-hidden="true" /></div>
    </section>

    <section className="login-panel">
      <div className="login-card">
        <p className="eyebrow">Area riservata</p>
        <h2>Accedi al tuo studio</h2>
        <p className="muted">Inserisci le tue credenziali per continuare.</p>

        <form onSubmit={submit}>
          <label className="login-field">Email
            <span className="login-input">
              <Mail size={17} />
              <input data-testid="login-email-input" type="email" autoComplete="email" placeholder="nome@studio.it"
                value={login.email} onChange={e => setLogin({ ...login, email: e.target.value })} />
            </span>
          </label>
          <label className="login-field">Password
            <span className="login-input">
              <Lock size={17} />
              <input data-testid="login-password-input" type={visible ? "text" : "password"} autoComplete="current-password"
                placeholder="La tua password" value={login.password} onChange={e => setLogin({ ...login, password: e.target.value })} />
              <button type="button" data-testid="login-toggle-password" aria-label="Mostra password" onClick={() => setVisible(v => !v)}>
                {visible ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </span>
          </label>

          <div className="login-row">
            <label className="login-remember" data-testid="login-remember-label">
              <input type="checkbox" data-testid="login-remember" checked={!!login.remember}
                onChange={e => setLogin({ ...login, remember: e.target.checked })} />Ricordami
            </label>
            <button type="button" className="login-link" data-testid="login-forgot" onClick={() => setForgot(f => !f)}>Password dimenticata?</button>
          </div>

          {forgot && <div className="login-hint" data-testid="login-forgot-hint">
            {sent
              ? "Controlla la posta: se l'indirizzo è registrato riceverai il collegamento per reimpostare la password. Vale 60 minuti e una sola volta."
              : <>Inserisci sopra la tua email e conferma: ti inviamo un collegamento sicuro per scegliere una nuova password.
                <button type="button" className="login-link" data-testid="login-forgot-send" disabled={sending}
                  style={{ display: "block", marginTop: 8 }} onClick={forgotSend}>
                  {sending ? "Invio…" : "Inviami il collegamento"}</button></>}
          </div>}

          {(error || googleError) && <div className="error" data-testid="login-error" style={{ marginTop: 16 }}>{error || googleError}</div>}

          <button data-testid="login-submit-button" className="primary login-submit">Accedi <ArrowRight size={17} /></button>
        </form>

        <div className="login-divider">oppure</div>
        <button type="button" className="login-google" data-testid="login-google-button" onClick={google}>
          <GoogleMark />Accedi con Google
        </button>

        <div className="login-foot-bar">
          <span className="login-foot-links">
            <a href="/privacy">Privacy</a><i>|</i><a href="/prezzi">Prezzi</a><i>|</i><a href="/termini">Termini di servizio</a><i>|</i><a href="/supporto">Supporto</a>
          </span>
          <span>© 2026 ingego. Tutti i diritti riservati.</span>
        </div>
      </div>
    </section>
  </main>;
}
